<script setup>
import { computed, ref, watch } from "vue";
import { formatMoney } from "../../utils/analyticsUtils.js";
import { useAnalyticsAI } from "../../composables/useAnalyticsAI.js";

// Props
const props = defineProps({
    daily: { type: Array, default: () => [] },
    monthly: { type: Array, default: () => [] },
    yearly: { type: Array, default: () => [] },
});

// AI composable
const { forecastDaily, forecastMonthly, generateForecast, aiLoading } = useAnalyticsAI();

// Trigger AI forecast when source data changes
watch([
    () => props.daily,
    () => props.monthly,
    () => props.yearly,
], () => {
    generateForecast(props.daily, props.monthly, props.yearly);
}, { immediate: true });

// Linear regression for trend
const trendData = computed(() => {
    const data = props.daily || [];
    if (data.length < 2) return { line: [], slope: 0, intercept: 0, points: [] };

    const n = data.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    const points = data.map((d, i) => {
        const y = Number(d.total || d.amount || 0);
        sumX += i;
        sumY += y;
        sumXY += i * y;
        sumXX += i * i;
        return { x: i, y, date: d.date };
    });
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const line = points.map(p => ({ x: p.x, y: slope * p.x + intercept }));
    return { line, slope, intercept, points };
});

// Simple Z‑score anomaly detection
const anomalies = computed(() => {
    const pts = trendData.value.points || [];
    if (pts.length < 4) return [];
    const values = pts.map(p => p.y);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);
    return pts.filter(p => Math.abs(p.y - mean) > 2 * stdDev)
        .map(p => ({ ...p, type: p.y > mean ? 'high' : 'low' }));
});

// Prediction summary (actual + AI forecast)
const predictedMonthEnd = computed(() => {
    const actualTotal = trendData.value.points.reduce((s, p) => s + p.y, 0);
    const forecastTotal = forecastDaily.value.reduce((s, f) => s + Number(f.amount || 0), 0);
    return actualTotal + forecastTotal;
});

const growthVelocity = computed(() => trendData.value.slope);

// Chart dimensions
const chartWidth = 600;
const chartHeight = 200;
const padding = 20;

// Combine actual points with forecast points for scaling
const combinedPoints = computed(() => {
    const actual = trendData.value.points || [];
    const forecast = forecastDaily.value.map((f, idx) => ({
        x: actual.length + idx,
        y: Number(f.amount || 0),
        date: f.date,
        isForecast: true,
    }));
    return actual.concat(forecast);
});

// Scales based on combined points
const scales = computed(() => {
    const pts = combinedPoints.value;
    if (!pts.length) return { getX: () => 0, getY: () => 0 };
    const maxY = Math.max(...pts.map(p => p.y)) * 1.2; // 20% headroom
    const getX = i => padding + (i / (pts.length - 1)) * (chartWidth - 2 * padding);
    const getY = v => chartHeight - padding - (v / maxY) * (chartHeight - 2 * padding);
    return { getX, getY, maxY };
});

const yTicks = computed(() => {
    const m = scales.value.maxY || 0;
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => ({
        y: (i / steps) * m,
        label: formatMoney((i / steps) * m),
        pos: chartHeight - padding - ((i / steps) * m / m) * (chartHeight - 2 * padding),
    }));
});

// Path for actual trend line
const trendPath = computed(() => {
    const { line } = trendData.value;
    if (!line.length) return "";
    return "M " + line.map((p, i) => `${scales.value.getX(i)},${scales.value.getY(p.y)}`).join(" L ");
});

// Path for forecast line (dashed)
const forecastPath = computed(() => {
    if (!forecastDaily.value.length) return "";
    const startIdx = trendData.value.points.length;
    const pts = forecastDaily.value.map((f, i) => ({ x: startIdx + i, y: Number(f.amount || 0) }));
    return "M " + pts.map((p, i) => `${scales.value.getX(p.x)},${scales.value.getY(p.y)}`).join(" L ");
});

// Area path for actual data only
const dataPath = computed(() => {
    const pts = trendData.value.points || [];
    if (!pts.length) return "";
    let d = `M ${scales.value.getX(0)},${chartHeight - padding}`;
    pts.forEach((p, i) => {
        d += ` L ${scales.value.getX(i)},${scales.value.getY(p.y)}`;
    });
    d += ` L ${scales.value.getX(pts.length - 1)},${chartHeight - padding} Z`;
    return d;
});

const actualSmoothPath = computed(() => {
    const pts = trendData.value.points || [];
    if (pts.length < 2) return "";
    const toXY = (p) => [scales.value.getX(p.x), scales.value.getY(p.y)];
    const crToBezier = (p0, p1, p2, p3) => {
        const [x0, y0] = toXY(p0);
        const [x1, y1] = toXY(p1);
        const [x2, y2] = toXY(p2);
        const [x3, y3] = toXY(p3);
        const cp1x = x1 + (x2 - x0) / 6;
        const cp1y = y1 + (y2 - y0) / 6;
        const cp2x = x2 - (x3 - x1) / 6;
        const cp2y = y2 - (y3 - y1) / 6;
        return `C ${cp1x},${cp1y} ${cp2x},${cp2y} ${x2},${y2}`;
    };
    let d = `M ${toXY(pts[0]).join(',')}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        d += ` ${crToBezier(p0, p1, p2, p3)}`;
    }
    return d;
});

const forecastRegion = computed(() => {
    const actualCount = trendData.value.points.length;
    const totalCount = combinedPoints.value.length;
    if (actualCount === 0 || totalCount <= actualCount) return { x: 0, w: 0 };
    const xStart = scales.value.getX(actualCount - 1);
    const xEnd = scales.value.getX(totalCount - 1);
    return { x: xStart, w: Math.max(0, xEnd - xStart) };
});

// Tooltip handling
const tooltip = ref(null);
function showTooltip(e, point, type) {
    const rect = e.target.getBoundingClientRect();
    tooltip.value = {
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
        label: type === 'anomaly' ? 'Anomalía Detectada' : new Date(point.date).toLocaleDateString(),
        value: formatMoney(point.y ?? point.amount ?? 0),
    };
}
</script>

<template>
    <section class="panel predictions-panel">
        <div class="panel-head"><b><span class="material-symbols-rounded icon">query_stats</span> Analítica Avanzada
                &amp; Predicciones</b>
            <div class="legend">
                <span class="dot actual"></span>Actual
                <span class="dot trend"></span>Tendencia
                <span class="dot forecast"></span>Predicción
                <span class="dot anomaly"></span>Anomalía
            </div>
        </div>
        <div class="grid-layout">
            <div class="stats-col">
                <div class="stat-card">
                    <div class="label">Proyección Cierre Mes</div>
                    <div class="value highlight">{{ formatMoney(predictedMonthEnd) }}</div>
                    <div class="sub">Basado en tendencia + IA</div>
                </div>
                <div class="stat-card">
                    <div class="label">Velocidad de Crecimiento</div>
                    <div class="value" :class="growthVelocity >= 0 ? 'pos' : 'neg'">
                        {{ growthVelocity >= 0 ? '+' : '' }}{{ formatMoney(growthVelocity) }}/día
                    </div>
                    <div class="sub">Promedio diario estimado</div>
                </div>
                <div class="stat-card">
                    <div class="label">Anomalías Detectadas</div>
                    <div class="value warning">{{ anomalies.length }}</div>
                    <div class="sub">Desviaciones &gt; 2σ</div>
                </div>
            </div>
            <div class="chart-col">
                <div class="chart-title">Tendencia de Ventas y Predicciones</div>
                <div class="svg-container">
                    <div v-if="aiLoading" class="overlay">
                        <div class="spinner"></div>
                        <span>Calculando pronóstico en tiempo real…</span>
                    </div>
                    <svg :viewBox="`0 0 ${chartWidth} ${chartHeight}`" preserveAspectRatio="none">
                        <line :x1="padding" :y1="chartHeight - padding" :x2="chartWidth - padding"
                            :y2="chartHeight - padding" stroke="var(--border)" />
                        <line :x1="padding" :y1="padding" :x2="padding" :y2="chartHeight - padding"
                            stroke="var(--border)" />
                        <line v-for="i in 5" :key="i" :x1="padding"
                            :y1="chartHeight - padding - (i / 5) * (chartHeight - 2 * padding)"
                            :x2="chartWidth - padding"
                            :y2="chartHeight - padding - (i / 5) * (chartHeight - 2 * padding)" stroke="var(--border)"
                            stroke-dasharray="4" opacity="0.5" />
                        <rect v-if="forecastRegion.w" :x="forecastRegion.x" :y="padding" :width="forecastRegion.w"
                            :height="chartHeight - 2 * padding" fill="url(#gradForecast)" opacity="0.12" />
                        <path :d="dataPath" fill="url(#gradArea)" opacity="0.3" />
                        <path :d="trendPath" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                        <path :d="actualSmoothPath" fill="none" stroke="var(--primary)" stroke-width="1.6"
                            opacity="0.85" stroke-linecap="round" stroke-linejoin="round" />
                        <path v-if="forecastPath" :d="forecastPath" fill="none" stroke="var(--accent)" stroke-width="2"
                            stroke-dasharray="2,4" opacity="0.6" />
                        <circle v-for="(p, i) in trendData.points" :key="'p' + i" :cx="scales.getX(i)"
                            :cy="scales.getY(p.y)" r="3" fill="var(--primary)"
                            @mouseenter="showTooltip($event, p, 'point')" @mouseleave="tooltip = null" />
                        <circle v-for="(f, i) in forecastDaily" :key="'f' + i"
                            :cx="scales.getX(trendData.points.length + i)" :cy="scales.getY(Number(f.amount))" r="3"
                            fill="var(--accent)"
                            @mouseenter="showTooltip($event, { date: f.date, amount: Number(f.amount) }, 'forecast')"
                            @mouseleave="tooltip = null" />
                        <circle v-for="(a, i) in anomalies" :key="'a' + i" :cx="scales.getX(a.x)" :cy="scales.getY(a.y)"
                            r="6" fill="none" stroke="#ef4444" stroke-width="2" class="pulse"
                            @mouseenter="showTooltip($event, a, 'anomaly')" @mouseleave="tooltip = null" />
                        <g>
                            <text v-for="(t, i) in yTicks" :key="'yt' + i" :x="8" :y="t.pos + 4" fill="var(--muted)"
                                font-size="10">{{ t.label }}</text>
                        </g>
                        <defs>
                            <linearGradient id="gradArea" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stop-color="var(--primary)" stop-opacity="0.5" />
                                <stop offset="100%" stop-color="var(--primary)" stop-opacity="0" />
                            </linearGradient>
                            <linearGradient id="gradForecast" x1="1" y1="0" x2="0" y2="0">
                                <stop offset="0%" stop-color="var(--accent)" stop-opacity="0.5" />
                                <stop offset="100%" stop-color="var(--accent)" stop-opacity="0" />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>
            </div>
        </div>
        <!-- Tooltip -->
        <div v-if="tooltip" class="tooltip" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
            <div class="t-head">{{ tooltip.label }}</div>
            <div class="t-val">{{ tooltip.value }}</div>
        </div>
    </section>
</template>

<style scoped>
.predictions-panel {
    border: 1px solid var(--border);
    background: var(--surface);
    border-radius: 16px;
    padding: 1.2rem;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.icon {
    color: #8b5cf6;
    margin-right: 8px;
}

.grid-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 1rem;
}

@media (min-width: 900px) {
    .grid-layout {
        grid-template-columns: 240px 1fr;
    }
}

.stats-col {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.stat-card {
    background: var(--bg);
    border-radius: 12px;
    padding: 1rem;
    border: 1px solid var(--border);
}

.label {
    font-size: 0.85rem;
    color: var(--muted);
    margin-bottom: 0.4rem;
}

.value {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text);
}

.value.highlight {
    color: #8b5cf6;
}

.value.pos {
    color: #10b981;
}

.value.neg {
    color: #ef4444;
}

.value.warning {
    color: #f59e0b;
}

.sub {
    font-size: 0.75rem;
    color: var(--muted);
    margin-top: 0.2rem;
}

.chart-col {
    display: flex;
    flex-direction: column;
}

.chart-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 1rem;
}

.svg-container {
    flex: 1;
    min-height: 200px;
    position: relative;
}

svg {
    width: 100%;
    height: 100%;
    overflow: visible;
}

.legend {
    display: flex;
    gap: 12px;
    align-items: center;
    font-size: 0.75rem;
    color: var(--muted);
}

.legend .dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
}

.legend .actual {
    background: var(--primary);
}

.legend .trend {
    background: var(--accent);
}

.legend .forecast {
    background: transparent;
    border: 2px dashed var(--accent);
    width: 12px;
    height: 12px;
    border-radius: 50%;
}

.legend .anomaly {
    background: #ef4444;
}

.overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(0, 0, 0, 0.04);
    z-index: 2;
    border-radius: 8px;
    color: var(--muted);
}

.spinner {
    width: 16px;
    height: 16px;
    border: 3px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.pulse {
    animation: pulse-ring 2s infinite;
}

@keyframes pulse-ring {
    0% {
        stroke-width: 2;
        opacity: 1;
    }

    50% {
        stroke-width: 6;
        opacity: 0.5;
    }

    100% {
        stroke-width: 2;
        opacity: 1;
    }
}

.tooltip {
    position: fixed;
    transform: translate(-50%, -100%);
    background: rgba(15, 23, 42, 0.95);
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.8rem;
    pointer-events: none;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(4px);
}

.t-head {
    font-weight: 600;
    margin-bottom: 2px;
    color: #cbd5e1;
}

.t-val {
    font-weight: 700;
}

/* Responsive Design */
@media (max-width: 768px) {
    .predictions-panel {
        padding: 1rem;
        box-sizing: border-box;
    }

    .panel-head {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.4rem;
        width: 100%;
    }

    .panel-head b {
        font-size: 0.85rem;
        max-width: 100%;
        white-space: normal;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    .icon {
        font-size: 16px;
    }

    .legend {
        flex-wrap: wrap;
        gap: 6px;
        font-size: 0.65rem;
        width: 100%;
    }

    .grid-layout {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
    }

    .stats-col {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.65rem;
        width: 100%;
        box-sizing: border-box;
    }

    .stat-card {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        padding: 0.85rem;
    }

    .label {
        font-size: 0.8rem;
        word-wrap: break-word;
    }

    .value {
        font-size: 1.2rem;
        word-wrap: break-word;
    }

    .sub {
        font-size: 0.7rem;
    }

    .chart-col {
        width: 100%;
    }

    .chart-title {
        font-size: 0.9rem;
    }

    .svg-container {
        min-height: 200px;
        width: 100%;
    }

    svg {
        width: 100%;
        height: 100%;
    }
}

@media (max-width: 480px) {
    .predictions-panel {
        padding: 0.75rem;
        box-sizing: border-box;
    }

    .icon {
        font-size: 14px;
    }

    .panel-head {
        gap: 0.3rem;
    }

    .panel-head b {
        font-size: 0.8rem;
        white-space: normal;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    .legend {
        gap: 5px;
        font-size: 0.6rem;
    }

    .legend .dot {
        width: 7px;
        height: 7px;
        margin-right: 3px;
    }

    .legend .forecast {
        width: 9px;
        height: 9px;
    }

    .grid-layout {
        gap: 1rem;
    }

    .stats-col {
        gap: 0.5rem;
    }

    .stat-card {
        padding: 0.65rem;
        max-width: 100%;
        box-sizing: border-box;
    }

    .label {
        font-size: 0.75rem;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }

    .value {
        font-size: 1rem;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }

    .sub {
        font-size: 0.65rem;
    }

    .chart-title {
        font-size: 0.85rem;
    }

    .svg-container {
        min-height: 180px;
    }

    svg text {
        font-size: 7px;
    }

    .overlay {
        font-size: 0.75rem;
    }

    .spinner {
        width: 12px;
        height: 12px;
    }

    .tooltip {
        font-size: 0.7rem;
        padding: 4px 7px;
    }

    .t-head {
        font-size: 0.65rem;
    }

    .t-val {
        font-size: 0.7rem;
    }
}
</style>
